import React, { createContext, useContext } from 'react';

/**
 * ConversationContext provides conversation data throughout the ChatSession component tree
 * so that error handlers can attach conversation context to Sentry reports.
 *
 * This is especially important for:
 * - Markdown rendering errors caught by SafeRenderer
 * - Any other rendering errors that occur during ChatSession processing
 */
const ConversationContext = createContext(null);

/**
 * Provider component to wrap ChatSession and provide conversation data
 *
 * @param {Object} props
 * @param {Array} props.turns - The conversation turns
 * @param {Object} props.searchParams - The search parameters (optional)
 * @param {Object} props.editedTemplates - The edited templates (optional)
 * @param {Object} props.sourceReferencesStore - The source references store (optional)
 * @param {React.ReactNode} props.children - Child components
 */
export const ConversationProvider = ({ turns, searchParams = null, editedTemplates = {}, sourceReferencesStore = null, isStreamReplaying = false, children }) => {
  const value = {
    turns,
    searchParams,
    editedTemplates,
    sourceReferencesStore,
    isStreamReplaying,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

/**
 * Hook to access conversation context
 *
 * @returns {Object|null} - The conversation context or null if not in a conversation
 */
export const useConversationContext = () => {
  return useContext(ConversationContext);
};

export default ConversationContext;
